import React from 'react';
import PropTypes from 'prop-types';
import UIHelper from '../../utils/UIHelper';
import ASSET_TYPES from '../../types/AssetTypes';
import mui from '../../utils/MuiIcons';
import Thumbnail from '../../components/Thumbnail';

/**
 * @typedef {import('../../types/ContentItems').ContentItem} ContentItem
 * @typedef {import('../../types/Sequences').SequenceEntry} SequenceEntry
 * @param {Object} props - The component props.
 * @param {SequenceEntry|ContentItem} props.item - The review item data, could be SequenceEntry or ContentItem. Asset if playlist is rendering it's video assets
 * @param {number} props.index - The index of the review item.
 * @param {string} props.toggleClass - Additional class name to manage row toggling only for playlists.
 * @returns {JSX.Element} The rendered table row element.
 */
function ReviewRow({ item, index, toggleClass = '' }) {
  const asset = item?.asset || item; // Support both SequenceEntry and Asset types
  const isPlaylist = asset.type === ASSET_TYPES.playlist;
  const isVideo = asset.type === ASSET_TYPES.video;
  const playlistEntries = asset.files || []; // For playlists, this will contain the video entries; for non-playlists, it will be empty
  const classToBeToggled = `toggle-row-${index}`;
  /**
   * Renders the resolution block for the item.
   * @returns {JSX.Element|null}
   */
  const getResolution = () => (
    <div>
      {asset.resolutionMismatch && (
        <span
          className={`${mui.className} icon-warning`}
          title="Resolution mismatch"
          data-test="resolution-warning"
        >
          {mui.icons.warning_mark}
        </span>
      )}{' '}
      <span data-test="review-table-row-resolution">
        {UIHelper.asset.formatResolution(asset.width, asset.height)}
      </span>
    </div>
  );

  /**
   * Toggles the visibility of playlist entries when a playlist row is clicked. It adds/removes the 'closed' class to the rows corresponding to the playlist entries.
   * This function is only relevant for playlist items, and the toggleClass prop manages the initial state of these rows (open or closed).
   * @param {string} rowClassIdentifier - The class name that identifies which rows to toggle (e.g., 'toggle-row-1').
   */
  const toggleHandler = rowClassIdentifier => {
    // if called
    const rows = document.getElementsByClassName(rowClassIdentifier);

    for (const element of rows) {
      element.classList.toggle('closed');
    }
  };

  /**
   * Returns the onClick handler for the row. Only playlists are clickable to toggle their entries. Non-playlist items return null for onClick.
   * @returns {Function|null}
   */
  const onClickHandler = isPlaylist ? () => toggleHandler(classToBeToggled) : null;
  /**
   * Formats the duration for display. Uses UIHelper to maintain consistent formatting across the app.
   * @returns {JSX.Element} Formatted duration element (e.g., "<span>5s</span>").
   */
  const getDuration = () => <div>{UIHelper.time.formatDuration(asset.duration).formatted}</div>;
  /**
   * Renders the thumbnail for the asset. For playlists, this could be a generic playlist thumbnail; for videos/images, it will show the actual thumbnail.
   * @returns {JSX.Element}
   */
  const getThumbnail = () => (
    <div>
      <Thumbnail asset={asset} />
    </div>
  );
  /**
   * Renders the asset name, using UIHelper to maintain consistent display logic (e.g., handling long names, special characters).
   * @returns {JSX.Element}
   */
  const getName = () => (
    <div>{UIHelper.asset.getAssetNameEle(asset, 'review-table-row', true)}</div>
  );
  /**
   * Renders the asset type icon using MuiIcons, providing a visual indicator of the asset type (video, image, playlist, etc.).
   * @returns {JSX.Element}
   */
  const getType = () => (
    <div className="review-table-asset-type">
      <span className={mui.className} title={asset.type}>
        {mui.icons[asset.type]}
      </span>
    </div>
  );
  /**
   * Renders the index column content, which includes the index number and, if the item is a playlist, a toggle icon. For videos, it also includes a connector line to visually group them under the playlist.
   * @returns {JSX.Element}
   */
  const getIdxCol = () => (
    <div>
      {index}
      {isPlaylist && (
        <span className={`${classToBeToggled} toggle-icon`} aria-label="Toggle playlist">
          &nbsp;▶
        </span>
      )}
      {isVideo && <span className="child-connector">&#9492;&#8883;</span>}
    </div>
  );
  /**
   * Returns the metadata display for the review asset.
   * @returns {JSX.Element} Folder, tags, and validity details for the current asset.
   */
  const getMetaData = () => (
    <div>
      <div className="undertext">Folder: {asset.parent || '/'}</div>
      {asset.tags && asset.tags.length > 0 && (
        <div className="undertext">Tags: {asset.tags.join(', ')}</div>
      )}
      {Boolean(asset.validityEnabled) && (
        <p className="undertext darkblue">
          Validity: {UIHelper.time.formatDate(asset.validityStart)}
          {' - '}
          {UIHelper.time.formatDate(asset.validityEnd)}
        </p>
      )}
    </div>
  );

  return (
    <>
      <tr
        key={`row-${index}`}
        className={`${isPlaylist ? ' clickable' : ''} ${toggleClass}`}
        onClick={onClickHandler}
        data-id={asset.id}
        data-type={asset.type}
      >
        {/* Everything needs to be covered with a block element for the closed animation */}
        <td>{getIdxCol()}</td>
        <td>{getType()}</td>
        <td>{getThumbnail()}</td>
        <td>{getName()}</td>
        <td>{getDuration()}</td>
        <td>{getResolution()}</td>
        <td>{getMetaData()}</td>
      </tr>
      {isPlaylist &&
        playlistEntries.map((playlistVideoAsset, entryIdx) => (
          <ReviewRow
            key={`row-${index}-entry-${entryIdx}`}
            item={playlistVideoAsset}
            toggleClass={isPlaylist ? `toggle-row-${index} closed` : ''}
          />
        ))}
    </>
  );
}

ReviewRow.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number,
  toggleClass: PropTypes.string,
};

export default ReviewRow;
